import type { MouseEvent as ReactMouseEvent } from "react"
import { useNavigate } from "react-router-dom"

type UnsavedGuard = {
  isDirty: () => boolean
  requestLeave: (onConfirm: () => void) => void
}

let guard: UnsavedGuard | null = null

export const setUnsavedChangesGuard = (next: UnsavedGuard | null) => {
  guard = next
}

export const isUnsavedDirty = () => Boolean(guard?.isDirty())

export const confirmIfUnsaved = (onConfirm: () => void) => {
  if (!guard?.isDirty()) {
    onConfirm()
    return
  }
  guard.requestLeave(onConfirm)
}

const isModifiedClick = (event: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean; button: number }) =>
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey ||
  event.button !== 0

/** Call from a React Router `Link` onClick so SPA nav waits on the confirm modal. */
export const blockUnsavedLinkClick = (
  event: ReactMouseEvent<HTMLAnchorElement>,
  proceed: () => void
) => {
  if (!guard?.isDirty() || isModifiedClick(event) || event.defaultPrevented) {
    return
  }
  event.preventDefault()
  guard.requestLeave(proceed)
}

export const useGuardLinkClick = (to: string, afterClick?: () => void) => {
  const navigate = useNavigate()
  return (event: ReactMouseEvent<HTMLAnchorElement>) => {
    afterClick?.()
    blockUnsavedLinkClick(event, () => navigate(to))
  }
}
